import { useCallback, useEffect } from 'react'

import { LinkOutbound } from '@/components/common/Link'
import { AD_CONFIG, type AdIdType, type AdUnitType } from '@/defs/ad'
import { type UtmType } from '@/defs/misc'
import { useImpression } from '@/hooks/useImpression'
import { useReduxSelector } from '@/store/hooks'
import { trackEvent } from '@/utils/analyticsUtil'
import { cn } from '@/utils/commonUtil'

import styles from './AdInHouse.module.scss'

type Props = {
  id?: AdIdType | null
  padding?: 'none'
  unit: AdUnitType
  utm?: UtmType
  variant?: 'poster'
}

export const AdInHouse = ({ id, padding, unit, utm, variant }: Props) => {
  const userStatus = useReduxSelector((s) => s.user.status)
  const isLoggedIn = userStatus === 'signedIn'

  const onImpression = useCallback(() => {
    if (!id) return
    trackEvent({ action: 'inhouse_ad_impression', category: 'ad', id, nonInteraction: true, unit })
  }, [id, unit])

  const impressionRef = useImpression({ onImpression })
  const ref = id ? impressionRef : undefined

  useEffect(() => {
    if (!id || userStatus === 'loading') return
    trackEvent({ action: 'load', category: 'ad', label: id, nonInteraction: true })
    if (isLoggedIn) {
      trackEvent({ action: 'load_loggedin', category: 'ad', label: id, nonInteraction: true })
    }
  }, [id, isLoggedIn, userStatus])

  useEffect(() => {
    if (!id || userStatus === 'loading') return
    trackEvent({
      action: 'inhouse_ad_loaded',
      category: 'ad',
      id,
      nonInteraction: true,
      unit,
    })
  }, [id, unit, userStatus])

  const { alt, src, url } = (id && AD_CONFIG[id]) ?? {}

  return (
    <div className={cn(styles.container, { padding, styles, variant })} ref={ref}>
      {!src || !url ? (
        <div className={styles.ad} />
      ) : (
        <LinkOutbound className={styles.ad} href={url} utm={utm}>
          <img alt={alt} decoding='async' loading='lazy' src={src} />
        </LinkOutbound>
      )}
    </div>
  )
}
